// components/admin/ReportingView.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { Role, Run, Module } from '../../types';
import { reportingService } from '../../services/reportingService';
import { useAuth } from '../../contexts/AuthContext';

interface ReportingFilters {
  startDate: string;
  endDate: string;
  selectedTenantIds: string[];
}

const thirtyDaysAgo = new Date();
thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

const defaultFilters: ReportingFilters = {
  startDate: thirtyDaysAgo.toISOString().split('T')[0],
  endDate: new Date().toISOString().split('T')[0],
  selectedTenantIds: [],
};

const downloadTextFile = (filename: string, content: string, mime = 'text/csv;charset=utf-8') => {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};
const normalizeTenantName = (t: any) =>
  t?.tenantName || t?.name || t?.displayName || t?.schoolName || t?.id || 'Unknown';
const normalizeTenantId = (t: any) =>
  String(t?.id || t?.tenantId || t?.tenantID || t?.docId || '').trim();
const normRunId = (r: any) => String(r?.id || r?.runId || '').trim();

const normPages = (r: any) => {
  const top = Number(r?.inputPages || 0);
  const meta = Number(r?.inputMeta?.inputPages || 0);
  const billed = Number(r?.billing?.inputPages || 0);
  return top || meta || billed || 0;
};

const sumByBatch = (byBatch: any[], field: string) => {
  if (!Array.isArray(byBatch)) return 0;
  return byBatch.reduce((acc, x) => acc + Number(x?.[field] || 0), 0);
};

const normTokens = (r: any) => {
  const um = r?.usageMetadata || null;

  // byBatch rollup for atypical
  const byBatch = um?.byBatch;
  if (Array.isArray(byBatch) && byBatch.length) {
    const prompt = sumByBatch(byBatch, 'promptTokenCount');
    const out = sumByBatch(byBatch, 'candidatesTokenCount');
    const total = sumByBatch(byBatch, 'totalTokenCount') || (prompt + out);
    return { prompt, out, total };
  }

  const prompt = Number(um?.promptTokenCount || 0);
  const out = Number(um?.candidatesTokenCount || 0);
  const totalFromApi = Number(um?.totalTokenCount || 0);
  const total = (prompt + out) > 0 ? (prompt + out) : totalFromApi;

  return { prompt, out, total };
};

const normBilledToType = (r: any) => {
  const t1 = String(r?.billedTo?.type || '').trim().toUpperCase();
  if (t1 === 'TENANT' || t1 === 'PARTNER') return t1;

  const t2 = String(r?.billingType || '').trim().toUpperCase();
  if (t2 === 'TENANT' || t2 === 'PARTNER') return t2;

  const t3 = String(r?.billing?.type || '').trim().toUpperCase();
  if (t3 === 'TENANT' || t3 === 'PARTNER') return t3;

  return '';
};

const billedToLabel = (r: any) => {
  const t = normBilledToType(r);
  if (!t) return '';
  if (t === 'PARTNER') return `PARTNER: ${String(r?.billedTo?.partnerId || r?.actorPartnerId || '')}`;
  return `TENANT: ${String(r?.billedTo?.tenantId || r?.tenantId || '')}`;
};

const billedToPayerId = (r: any) => {
  const t = normBilledToType(r);
  if (t === 'PARTNER') return String(r?.billedTo?.partnerId || r?.actorPartnerId || '').trim();
  if (t === 'TENANT') return String(r?.billedTo?.tenantId || r?.tenantId || '').trim();
  return '';
};

const csvEscape = (v: any) => {
  const s = String(v ?? '');
  if (s.includes('"') || s.includes(',') || s.includes('\n') || s.includes('\r')) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
};

const toIsoLike = (v: any) => {
  try {
    if (!v) return '';
    if (typeof v.toDate === 'function') {
      const d = v.toDate();
      return Number.isFinite(d.getTime()) ? d.toISOString() : '';
    }
    const secA = typeof v?._seconds === 'number' ? v._seconds : null;
    const nsecA = typeof v?._nanoseconds === 'number' ? v._nanoseconds : 0;
    const secB = typeof v?.seconds === 'number' ? v.seconds : null;
    const nsecB = typeof v?.nanoseconds === 'number' ? v.nanoseconds : 0;

    const seconds = secA ?? secB;
    const nanos = secA != null ? nsecA : (secB != null ? nsecB : 0);

    if (typeof seconds === 'number' && Number.isFinite(seconds)) {
      const ms = seconds * 1000 + Math.floor(nanos / 1e6);
      const d = new Date(ms);
      return Number.isFinite(d.getTime()) ? d.toISOString() : '';
    }

    if (typeof v === 'number') {
      const d = new Date(v);
      return Number.isFinite(d.getTime()) ? d.toISOString() : '';
    }

    if (typeof v === 'string') {
      const d = new Date(v);
      return Number.isFinite(d.getTime()) ? d.toISOString() : '';
    }

    return '';
  } catch {
    return '';
  }
};

export const ReportingView: React.FC = () => {
  const { profile: userProfile, tenant, partnerTenantList, allTenantList } = useAuth();
  const canSeeTokenCost = userProfile?.role === Role.PLATFORM_ADMIN;

  const [filters, setFilters] = useState<ReportingFilters>(defaultFilters);
  const [selectedActorPartnerId, setSelectedActorPartnerId] = useState<string>('');
  const [selectedBilledToType, setSelectedBilledToType] = useState<string>(''); // "" | "TENANT" | "PARTNER"
  const [runs, setRuns] = useState<Run[]>([]);
  const [userDirectory, setUserDirectory] = useState<Record<string, { email?: string; displayName?: string }> | null>(
    null
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const formatRunTs = (v: any) => {
    try {
      if (!v) return '';

      if (typeof v.toDate === 'function') {
        const d = v.toDate();
        return Number.isFinite(d.getTime()) ? d.toLocaleString() : '';
      }

      const secA = typeof v?._seconds === 'number' ? v._seconds : null;
      const nsecA = typeof v?._nanoseconds === 'number' ? v._nanoseconds : 0;

      const secB = typeof v?.seconds === 'number' ? v.seconds : null;
      const nsecB = typeof v?.nanoseconds === 'number' ? v.nanoseconds : 0;

      const seconds = secA ?? secB;
      const nanos = secA != null ? nsecA : (secB != null ? nsecB : 0);

      if (typeof seconds === 'number' && Number.isFinite(seconds)) {
        const ms = seconds * 1000 + Math.floor(nanos / 1e6);
        const d = new Date(ms);
        return Number.isFinite(d.getTime()) ? d.toLocaleString() : '';
      }

      if (typeof v === 'number') {
        const d = new Date(v);
        return Number.isFinite(d.getTime()) ? d.toLocaleString() : '';
      }

      if (typeof v === 'string') {
        const d = new Date(v);
        return Number.isFinite(d.getTime()) ? d.toLocaleString() : '';
      }

      return '';
    } catch {
      return '';
    }
  };

  const visibleTenants = useMemo(() => {
    if (!userProfile) return [];

    if (userProfile.role === Role.PLATFORM_ADMIN) {
      return (allTenantList || [])
        .map(t => ({ id: normalizeTenantId(t), name: normalizeTenantName(t) }))
        .filter(t => !!t.id);
    }

    if (userProfile.role === Role.PARTNER_ADMIN) {
      const tenants = (partnerTenantList || [])
        .map(t => ({ id: normalizeTenantId(t), name: normalizeTenantName(t) }))
        .filter(t => !!t.id);

      return [{ id: "__PARTNER_ALL__", name: "All (My Partner)" }, ...tenants];
    }

    if (userProfile.role === Role.TENANT_ADMIN) {
      if (tenant?.id) return [{ id: tenant.id, name: normalizeTenantName(tenant) }];
      if (userProfile.tenantId) return [{ id: userProfile.tenantId, name: userProfile.tenantId }];
      return [];
    }

    return [];
  }, [userProfile, tenant, partnerTenantList, allTenantList]);

  useEffect(() => {
    const fetchData = async () => {
      if (!userProfile) return;

      setLoading(true);
      setError(null);

      try {
        let tenantIds =
          filters.selectedTenantIds.length > 0 ? filters.selectedTenantIds : visibleTenants.map(t => t.id);

        if (userProfile.role === Role.PARTNER_ADMIN) {
          if (tenantIds.includes("__PARTNER_ALL__")) {
            tenantIds = [];
          } else {
            tenantIds = tenantIds.filter(tid => tid !== "__PARTNER_ALL__");
          }
        }

        const data = await reportingService.fetchReportingData({
          startDate: filters.startDate,
          endDate: filters.endDate,
          tenantIds,
          userProfile,
        });

        const raw = Array.isArray(data.runs) ? data.runs : [];

        const byId = new Map<string, any>();
        for (const r of raw) {
          const id = normRunId(r);
          if (!id) continue;

          const prev = byId.get(id);
          if (!prev) {
            byId.set(id, r);
            continue;
          }

          const prevT = (prev?.completedAt?.toMillis?.() ?? prev?.createdAt?.toMillis?.() ?? 0) as number;
          const curT = (r?.completedAt?.toMillis?.() ?? r?.createdAt?.toMillis?.() ?? 0) as number;

          if (curT >= prevT) byId.set(id, r);
        }

        const deduped = Array.from(byId.values());

        deduped.sort((a, b) => {
          const at = a?.createdAt?.toMillis ? a.createdAt.toMillis() : 0;
          const bt = b?.createdAt?.toMillis ? b.createdAt.toMillis() : 0;
          return bt - at;
        });

        setRuns(deduped as any);
        setUserDirectory(data.userDirectory || null);
      } catch (err: any) {
        setRuns([]);
        setUserDirectory(null);
        setError(err?.message || 'Failed to fetch reporting data.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [filters, visibleTenants, userProfile]);

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;

    if (name === 'selectedTenantIds') {
      const options = (e.target as HTMLSelectElement).options;
      const selected: string[] = [];
      for (let i = 0; i < options.length; i++) {
        if (options[i].selected) selected.push(options[i].value);
      }
      setFilters(prev => ({ ...prev, selectedTenantIds: selected }));
      return;
    }

    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const tenantNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const t of visibleTenants) {
      const id = String(t?.id || "").trim();
      if (!id) continue;
      m.set(id, String(t?.name || "").trim() || id);
    }
    return m;
  }, [visibleTenants]);

  const availableActorPartners = useMemo(() => {
    if (!runs || runs.length === 0) return [];
    const set = new Set<string>();
    for (const r of runs as any[]) {
      const pid = String((r as any).actorPartnerId || '').trim();
      if (pid) set.add(pid);
    }
    return Array.from(set).sort();
  }, [runs]);

  const filteredRuns = useMemo(() => {
    let out: any[] = Array.isArray(runs) ? (runs as any[]) : [];

    if (selectedActorPartnerId) {
      out = out.filter(r => String(r.actorPartnerId || '').trim() === selectedActorPartnerId);
    }

    if (selectedBilledToType) {
      const want = String(selectedBilledToType).trim().toUpperCase();
      out = out.filter(r => normBilledToType(r) === want);
    }

    return out;
  }, [runs, selectedActorPartnerId, selectedBilledToType]);

  const stats = useMemo(() => {
    const totalCreditsConsumed = filteredRuns.reduce((acc, r: any) => acc + Number(r.creditsDeducted || 0), 0);
    const totalRuns = filteredRuns.length;

    const moduleCounts = filteredRuns.reduce((acc, r: any) => {
      const m = r.module as Module;
      acc[m] = (acc[m] || 0) + 1;
      return acc;
    }, {} as Record<Module, number>);

    const mostUsedModule =
      Object.entries(moduleCounts).sort((a, b) => (b[1] as number) - (a[1] as number))[0]?.[0] || 'N/A';

    return { totalCreditsConsumed, totalRuns, mostUsedModule };
  }, [filteredRuns]);

  const renderUserLabel = (run: any) => {
    const uid = String(run.userId || '');
    if (!uid) return '';
    const entry = userDirectory?.[uid];
    if (!entry) return uid;
    return entry.displayName || entry.email || uid;
  };

const buildExportRows = (list: any[]) => {
  return list.map((run: any) => {
    const t = normTokens(run);
    const billedType = normBilledToType(run);
    const billedToLabel =
      billedType === 'PARTNER'
        ? `PARTNER: ${String(run?.billedTo?.partnerId || run?.actorPartnerId || '')}`
        : billedType === 'TENANT'
          ? `TENANT: ${String(run?.billedTo?.tenantId || run?.tenantId || '')}`
          : '';

    // BASE ROW (always)
    const row: Record<string, any> = {
      Timestamp: formatRunTs(run.createdAt),
      User: renderUserLabel(run),
      Tenant: tenantNameById.get(String(run.tenantId || '').trim()) || String(run.tenantId || ''),
      Module: String(run.module || ''),
      Status: String(run.status || ''),
      'Billed To': billedToLabel,
      BillingType: String(run.billingType || ''),
      BillingCommitted: String(Boolean(run.billingCommitted)),
      'Input Pages': normPages(run),
      Credits: Number(run.creditsDeducted || 0),
    };

    // TOKEN COLUMNS (platform only)
    if (canSeeTokenCost) {
      const tokenCost =
        run.usageMetadata?.tokenCost ??
        run.tokenCost ??
        run.usageMetadata?.cost ??
        run.cost ??
        '';

      row['Prompt Tokens'] = t.prompt;
      row['Output Tokens'] = t.out;
      row['Total Tokens'] = t.total;
      row['Token Cost'] = tokenCost;
    }

    return row;
  });
};
const exportHeaders = useMemo(() => {
  const base = [
    'Timestamp',
    'User',
    'Tenant',
    'Module',
    'Status',
    'Billed To',
    'BillingType',
    'BillingCommitted',
    'Input Pages',
    'Credits',
  ];

  if (!canSeeTokenCost) return base;

  return [
    ...base,
    'Prompt Tokens',
    'Output Tokens',
    'Total Tokens',
    'Token Cost',
  ];
}, [canSeeTokenCost]);
const colSpan =
  7 + // Timestamp, User, Tenant, Module, Status, Input Pages, Credits
  (userProfile?.role === Role.PLATFORM_ADMIN ? 1 : 0) + // Billed To
  (canSeeTokenCost ? 4 : 0); // Prompt/Output/Total/Token Cost

  const exportCsv = () => {
  if (!userProfile) return;

  const isPlatform = userProfile.role === Role.PLATFORM_ADMIN;

  const header = [
    'Timestamp',
    'Run ID',
    'User',
    'User Email',
    'Tenant',
    'Tenant ID',
    'Module',
    'Status',
    'Billed To Type',
    'Billed To Payer ID',
    'Billed To Label',
    'Billing Committed',
    'Credits',
    'Input Pages',
    ...(isPlatform ? ['Prompt Tokens', 'Output Tokens', 'Total Tokens'] : []),
  ];

  const rows = filteredRuns.map((run: any) => {
    const uid = String(run.userId || '');
    const entry = uid ? userDirectory?.[uid] : null;

    const t = normTokens(run);
    const billedType = normBilledToType(run);
    const payerId = billedToPayerId(run);
    const label = billedToLabel(run);

    const tenantId = String(run.tenantId || '').trim();
    const tenantName = tenantNameById.get(tenantId) || tenantId;

    const base = [
      toIsoLike(run.createdAt),
      normRunId(run),
      entry?.displayName || entry?.email || uid,
      entry?.email || '',
      tenantName,
      tenantId,
      String(run.module || ''),
      String(run.status || ''),
      billedType,
      payerId,
      label,
      String(Boolean(run.billingCommitted)),
      String(Number(run.creditsDeducted || 0)),
      String(normPages(run)),
    ];

    const tokens = isPlatform ? [String(t.prompt), String(t.out), String(t.total)] : [];

    return [...base, ...tokens].map(csvEscape).join(',');
  });

  const csv = [header.map(csvEscape).join(','), ...rows].join('\n');

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);

  const fn = `osfn_runs_${filters.startDate}_to_${filters.endDate}_${String(userProfile.role)}.csv`;

  const a = document.createElement('a');
  a.href = url;
  a.download = fn;
  document.body.appendChild(a);
  a.click();
  a.remove();

  URL.revokeObjectURL(url);
};

  return (
    <div className="space-y-8">
      <div className="bg-white p-6 rounded-xl shadow-lg">
        <div className="flex items-center justify-between gap-4">
          <h2 className="text-xl font-bold">Filters</h2>

          <button
            type="button"
            onClick={exportCsv}
            className="px-4 py-2 rounded-md border border-gray-300 bg-white text-sm font-semibold"
          >
            Export CSV
          </button>
        </div>

        {userProfile?.role === Role.PLATFORM_ADMIN && (
          <div className="mt-4">
            <label className="block text-sm font-bold text-brand-grey mb-1">Billed To</label>
            <select
              value={selectedBilledToType}
              onChange={e => setSelectedBilledToType(e.target.value)}
              className="block w-full px-3 py-2 bg-white border border-gray-300 rounded-md"
            >
              <option value="">All</option>
              <option value="TENANT">Tenant</option>
              <option value="PARTNER">Partner</option>
            </select>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4">
          <div>
            <label className="block text-sm font-bold text-brand-grey mb-1">Start Date</label>
            <input
              type="date"
              name="startDate"
              value={filters.startDate}
              onChange={handleFilterChange}
              className="block w-full px-3 py-2 bg-white border border-gray-300 rounded-md"
            />
          </div>

          <div>
            <label className="block text-sm font-bold text-brand-grey mb-1">End Date</label>
            <input
              type="date"
              name="endDate"
              value={filters.endDate}
              onChange={handleFilterChange}
              className="block w-full px-3 py-2 bg-white border border-gray-300 rounded-md"
            />
          </div>

          {userProfile?.role === Role.PLATFORM_ADMIN && (
            <div>
              <label className="block text-sm font-bold text-brand-grey mb-1">Partner (actor)</label>
              <select
                value={selectedActorPartnerId}
                onChange={e => setSelectedActorPartnerId(e.target.value)}
                className="block w-full px-3 py-2 bg-white border border-gray-300 rounded-md"
              >
                <option value="">All Partners</option>
                {availableActorPartners.map(pid => (
                  <option key={pid} value={pid}>
                    {pid}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="block text-sm font-bold text-brand-grey mb-1">
              Tenants (multi-select) | visible: {visibleTenants.length}
            </label>

            <select
              multiple
              name="selectedTenantIds"
              value={filters.selectedTenantIds}
              onChange={handleFilterChange}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md h-32"
              style={{ backgroundColor: '#FFFFFF', color: '#111111' }}
            >
              {visibleTenants.map(t => (
                <option key={t.id} value={t.id} style={{ backgroundColor: '#FFFFFF', color: '#111111' }}>
                  {t.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-xl shadow-lg text-center">
          <p className="text-4xl font-bold text-brand-orange">{stats.totalCreditsConsumed.toLocaleString()}</p>
          <p className="text-gray-500">Credits Consumed</p>
        </div>
        <div className="bg-white p-6 rounded-xl shadow-lg text-center">
          <p className="text-4xl font-bold text-brand-orange">{stats.totalRuns.toLocaleString()}</p>
          <p className="text-gray-500">Runs Initiated</p>
        </div>
        <div className="bg-white p-6 rounded-xl shadow-lg text-center">
          <p className="text-4xl font-bold text-brand-orange capitalize">{stats.mostUsedModule}</p>
          <p className="text-gray-500">Most Used Module</p>
        </div>
      </div>

      <div className="bg-white p-6 rounded-xl shadow-lg">
        {loading ? (
          <p>Loading data...</p>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">{error}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Timestamp</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tenant</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Module</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>

                  {userProfile?.role === Role.PLATFORM_ADMIN && (
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Billed To</th>
                  )}

                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Input Pages</th>

                  {canSeeTokenCost && (
                    <>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Prompt Tokens</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Output Tokens</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total Tokens</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Token Cost</th>
                    </>
                  )}

                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Credits</th>
                </tr>
              </thead>

              <tbody className="bg-white divide-y divide-gray-200">
                {filteredRuns.map((run: any) => {
                  const t = normTokens(run);
                  const promptTokens = t.prompt;
                  const outputTokens = t.out;
                  const totalTokens = t.total;

                  const tokenCost =
                    run.usageMetadata?.tokenCost ??
                    run.tokenCost ??
                    run.usageMetadata?.cost ??
                    run.cost ??
                    '';

                  return (
                    <tr key={`${normRunId(run)}__${String(run.createdAt?._seconds || run.createdAt?.seconds || '')}`}>
                      <td className="px-4 py-4 text-sm">{formatRunTs(run.createdAt)}</td>
                      <td className="px-4 py-4 text-sm">{renderUserLabel(run)}</td>
                      <td className="px-4 py-4 text-sm">
                        {tenantNameById.get(String(run.tenantId || "").trim()) || String(run.tenantId || "")}
                      </td>
                      <td className="px-4 py-4 text-sm capitalize">{run.module}</td>
                      <td className="px-4 py-4 text-sm">
                        <span
                          className={`px-2 inline-flex text-xs font-semibold rounded-full ${
                            run.status === 'COMPLETED' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {run.status}
                        </span>
                      </td>

                      {userProfile?.role === Role.PLATFORM_ADMIN && (
                        <td className="px-4 py-4 text-sm">{billedToLabel(run)}</td>
                      )}

                      <td className="px-4 py-4 text-sm text-right">{normPages(run).toLocaleString()}</td>

                      {canSeeTokenCost && (
                        <>
                          <td className="px-4 py-4 text-sm text-right">{promptTokens}</td>
                          <td className="px-4 py-4 text-sm text-right">{outputTokens}</td>
                          <td className="px-4 py-4 text-sm text-right">{totalTokens}</td>
                          <td className="px-4 py-4 text-sm text-right">{tokenCost}</td>
                        </>
                      )}

                      <td className="px-4 py-4 text-sm text-right">{Number(run.creditsDeducted || 0)}</td>
                    </tr>
                  );
                })}

                {filteredRuns.length === 0 && (
                  <tr>
                    <td colSpan={colSpan} className="text-center py-8 text-gray-500">
                      No runs found for the selected filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};